// src/hooks/useAssessmentLogic.js

import { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth } from '../config/firebase-config';
import { doc, getDoc, addDoc, collection, setDoc, deleteField } from 'firebase/firestore';
import { ASSESSMENT_LIBRARY } from '../data/assessment-config';
import { SecureLogger } from '../utils/logger';
import { calculateScores } from '../utils/scoring';
import { resolveAssessmentId, audienceAllows, fallbackIdFor } from '../utils/resolveAssessmentId';
import { resolveSport, fillSportDeep } from '../data/sports';
import { resolveAgeTier } from '../utils/ageTier';

export const useAssessmentLogic = (currentUser, isPulseMode = false) => {
    // --- STATE ---
    const [stage, setStage] = useState('booting');
    const [config, setConfig] = useState(null);
    const [sessionId] = useState(() => `assess-session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const [minDelayPassed, setMinDelayPassed] = useState(false);
    
    const [agentContext, setAgentContext] = useState({ mode: 'intro', targetMetric: null });
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data Stores
    const [baselineAnswers, setBaselineAnswers] = useState({});
    const [radarScores, setRadarScores] = useState({
        focus: 50, resilience: 50, motivation: 50, confidence: 50, discipline: 50,
        awareness: 50, adaptability: 50, sleep_perf: 50, nutrition: 50, injury_mgmt: 50
    });
    const [triangulatedScores, setTriangulatedScores] = useState({});
    const [gameScores, setGameScores] = useState(null);
    // Scores of the assessment just submitted — feeds the completion screen so
    // the "what next" step is built from fresh numbers, not the previous profile.
    const [submittedStats, setSubmittedStats] = useState(null);
    const gameScoresRef = useRef(null);

    // --- 1. BOOT SEQUENCE ---
    useEffect(() => {
        const timer = setTimeout(() => setMinDelayPassed(true), 150); // the start screen is already on screen; this only spaces the config fetch from mount
        return () => clearTimeout(timer);
    }, []);

    // --- 2. CONFIG FETCHING ---
    useEffect(() => {
        let isMounted = true;

        const fetchConfig = async () => {
            let sportOrg = null; // the org whose sport words fill the config
            let loadedConfig;

            // ==========================================
            // THE DYNAMIC MICRO-PULSE INJECTOR
            // ==========================================
            if (isPulseMode) {
                // 1. Deep copy the skeleton: the youth pulse for a player under 18
                const pulseTier = resolveAgeTier(currentUser);
                const pulseIsYouth = pulseTier === 'kid' || pulseTier === 'teen';
                loadedConfig = JSON.parse(JSON.stringify(ASSESSMENT_LIBRARY[pulseIsYouth ? 'micro_pulse_youth_v1' : 'micro_pulse_v1']));
                
                // 2. Calculate the user's current scores
                const scores = calculateScores(currentUser);
                
                // 3. Find their lowest metric, but EXCLUDE sleep and anxiety
                const eligibleMetrics = scores.insights?.low?.filter(
                    m => m.id !== 'sleep_perf' && m.id !== 'anxiety'
                ) || [];
                
                // 4. Fallback to Focus if they somehow have no low metrics
                const targetMetric = eligibleMetrics.length > 0 
                    ? eligibleMetrics[0] 
                    : { id: 'focus', label: 'Mental Focus' };

                // 5. OVERWRITE THE PLACEHOLDER (keeping the youth labels, if any)
                const placeholder = loadedConfig.screener[0].questions[2] || {};
                loadedConfig.screener[0].questions[2] = {
                    id: `pulse_dynamic_${targetMetric.id}`,
                    text: pulseIsYouth
                        ? `This week, how did you do with ${String(targetMetric.label).toLowerCase()}?`
                        : `How are you managing your ${targetMetric.label} this week?`,
                    type: "slider_100",
                    metric: targetMetric.id,
                    ...(Array.isArray(placeholder.rangeLabels) ? { rangeLabels: placeholder.rangeLabels } : {})
                };
                
                if (isMounted) setConfig(loadedConfig);
                return; 
            }

            // ==========================================
            // NORMAL DEEP-DIVE FETCH LOGIC
            // ==========================================
            loadedConfig = ASSESSMENT_LIBRARY['core_v1']; 

            try {
                let orgData = null;

                if (auth.currentUser) {
                    let organizationId = currentUser?.organizationId;

                    if (!organizationId) {
                        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                        if (userSnap.exists()) organizationId = userSnap.data().organizationId;
                    }

                    if (organizationId) {
                        const orgSnap = await getDoc(doc(db, "organizations", organizationId));
                        if (orgSnap.exists()) { orgData = orgSnap.data(); sportOrg = orgData; }
                    }
                }

                // Age-gated routing: adults take the org's configured assessment;
                // minors (kid/teen) are routed to an age-appropriate, clinically
                // safe config regardless of the org default. See resolveAssessmentId.
                const tier = resolveAgeTier(currentUser);
                const targetConfigId = resolveAssessmentId({ ageTier: tier, org: orgData, memberType: currentUser?.memberType });

                // Database first (what the Assessment Builder saves), then the
                // hardcoded library as a fallback.
                const loadById = async (id) => {
                    try {
                        const snap = await getDoc(doc(db, "assessment_configs", id));
                        if (snap.exists()) {
                            SecureLogger.info(`Using Database Config: ${id}`);
                            return snap.data();
                        }
                    } catch (e) {
                        console.warn("Could not fetch config from DB, falling back to file.", e);
                    }
                    if (ASSESSMENT_LIBRARY[id]) {
                        SecureLogger.info(`Using Hardcoded Config: ${id}`);
                        return ASSESSMENT_LIBRARY[id];
                    }
                    return null;
                };

                // AUDIENCE SEPARATOR (see resolveAssessmentId.js). Whatever the org
                // is set to, an adult is never served a Youth set and a minor is
                // only ever served a Youth set. A mismatch falls back to the
                // platform default for the member's tier.
                let candidate = await loadById(targetConfigId);
                if (candidate && !audienceAllows(candidate, tier)) {
                    const fallbackId = fallbackIdFor(tier);
                    console.warn(`Assessment ${targetConfigId} is for audience '${candidate.audience}'; serving ${fallbackId} to this ${tier || 'adult'} member instead.`);
                    candidate = await loadById(fallbackId);
                }
                if (candidate) loadedConfig = candidate;

            } catch (error) {
                SecureLogger.error("Config Load Critical Fail", error);
            }

            // Sport words ({sport}, {surface}, ...) in question text follow the org (data/sports.js).
            if (isMounted) setConfig(fillSportDeep(loadedConfig, resolveSport(sportOrg)));
        };

        if (minDelayPassed) fetchConfig();

        return () => { isMounted = false; };
    }, [currentUser, minDelayPassed, isPulseMode]);

    // --- 3. TRANSITION LOGIC ---
    useEffect(() => {
        if (stage === 'booting' && minDelayPassed && config) {
            setStage('intro');
        }
    }, [stage, minDelayPassed, config]);

    // --- 4. CORE ALGORITHMS ---

    // Safely accepts overrideAnswers to prevent async React state race conditions
    const calculateClinicalTotals = (overrideAnswers = null) => {
        const activeAnswers = overrideAnswers || baselineAnswers;
        const totals = {};
        const sumQuestions = (qs) => qs.forEach(q => {
            if (q.metric && activeAnswers[q.id] !== undefined) {
                const raw = activeAnswers[q.id];
                let val;
                // B3 fix: select-type questions store the option LABEL (a string
                // like "More than 9 hours"), so parseInt() returned NaN and the
                // answer was silently dropped from the clinical sum (breaking
                // AUDIT-C and 2 of 5 sleep items). Scoring: an explicit
                // `optionScores` array (parallel to `options`) wins when present
                // — REQUIRED for non-monotonic items like sleep duration, where
                // both extremes are symptoms (U-shaped). Otherwise option ORDER
                // is the score (instruments list options best→worst), so label
                // → index. Stored answer format is unchanged; only summing fixes.
                // NOTE: `optionScores` is the Phase 4 {min,max,reverse} question
                // schema arriving early — Phase 4 absorbs it, not replaces it.
                if (q.type === 'select' && Array.isArray(q.options)) {
                    const idx = q.options.indexOf(raw);
                    if (idx >= 0) {
                        val = (Array.isArray(q.optionScores) && q.optionScores.length === q.options.length)
                            ? q.optionScores[idx]
                            : idx;
                    } else {
                        val = parseInt(raw); // numeric legacy fallback
                    }
                } else {
                    val = parseInt(raw);
                }
                if (!isNaN(val)) totals[q.metric] = (totals[q.metric] || 0) + val;
            }
        });
        
        config?.screener?.forEach(s => sumQuestions(s.questions));
        if (config?.modules) Object.values(config.modules).forEach(m => sumQuestions(m.questions));
        return totals;
    };

    const handleSubmit = async (overrideAnswers = null) => {
        setIsSubmitting(true);
        const user = auth.currentUser;
        if (!user) return;

        try {
            const isCognitiveEnabled = config?.configOptions?.includeCognitive !== false;
            // A set without the radar stage (the parent check-in) must not carry the
            // radar's untouched 50s into its scores as if they were answers.
            const radarEnabled = config?.configOptions?.includeRadar !== false;
            const validGameScores = gameScoresRef.current || gameScores;
            let reactionTime = (isCognitiveEnabled && validGameScores?.raw_hits > 0) 
                ? Math.round(45000 / validGameScores.raw_hits) : null;

            const existingStats = currentUser?.latestStats || {};
            const newPulseData = calculateClinicalTotals(overrideAnswers);

            // Performance metrics: the slider answers are the primary read; when the
            // member actually used the self-perception radar (any value moved off the
            // 50 default) each metric becomes the mean of slider and radar. Pulses
            // overwrite individual metrics with their own sliders as before.
            const radarTouched = Object.values(radarScores).some(v => v !== 50);
            const blendedPerf = {};
            if (!isPulseMode && radarEnabled) {
                Object.keys(radarScores).forEach(key => {
                    const slider = newPulseData[key];
                    const radar = radarScores[key];
                    if (typeof slider === 'number' && !isNaN(slider)) blendedPerf[key] = radarTouched ? Math.round((slider + radar) / 2) : slider;
                    else blendedPerf[key] = radar;
                });
            }

            const finalScores = isPulseMode ? {
                ...existingStats, 
                ...newPulseData,  
                cognitive_score: existingStats.cognitive_score || null,
                reaction_time: existingStats.reaction_time || null 
            } : { 
                ...(radarEnabled ? radarScores : {}),
                ...newPulseData,    
                ...blendedPerf,
                radar_used: radarEnabled && radarTouched,
                cognitive_score: isCognitiveEnabled ? (validGameScores?.score || 0) : null,
                reaction_time: reactionTime 
            };

            const payload = {
                userId: user.uid,
                userEmail: user.email,
                assessmentId: `assess_${Date.now()}`,
                completedAt: new Date().toISOString(),
                baseline_data: overrideAnswers || baselineAnswers,
                radar_data: (isPulseMode || !radarEnabled) ? null : radarScores,
                objective_data: (isCognitiveEnabled && !isPulseMode) ? validGameScores : null,
                scores: finalScores, 
                status: 'completed',
                isPulse: isPulseMode
            };

            SecureLogger.info("Submitting Payload", payload);
            await addDoc(collection(db, "assessments"), payload);

            // STREAK CALCULATION LOGIC WITH SPAM PROTECTION 
            const now = new Date();
            let newStreak = 1; 

            if (currentUser?.lastPulseDate) {
                const lastDate = new Date(currentUser.lastPulseDate);
                const diffDays = (now - lastDate) / (1000 * 60 * 60 * 24);
                
                if (diffDays <= 14) {
                    if (diffDays > 0.5) {
                        newStreak = (currentUser.pulseStreak || 0) + 1;
                    } else {
                        newStreak = currentUser.pulseStreak || 1;
                    }
                }
            }

            // Individual scores live in the self-only private_metrics subcollection
            // — never on the admin/coach-readable user doc.
            await setDoc(doc(db, "users", user.uid, "private_metrics", "latest"), {
                latestStats: finalScores,
                updatedAt: now.toISOString()
            }, { merge: true });

            await setDoc(doc(db, "users", user.uid), {
                lastAssessmentDate: isPulseMode ? (currentUser?.lastAssessmentDate || now.toISOString()) : now.toISOString(),
                lastPulseDate: now.toISOString(),
                pulseStreak: newStreak,
                latestStats: deleteField()
            }, { merge: true });

            setSubmittedStats(finalScores);
            setStage('complete');
        } catch (error) {
            SecureLogger.error("Submission Failed", error);
            alert("Save failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

        const analyzeResults = () => {
        // A. Baseline Metrics
        const baselineMetrics = {};
        if (config?.screener) {
            config.screener.forEach(sec => sec.questions.forEach(q => {
                if (q.metric && baselineAnswers[q.id] !== undefined) baselineMetrics[q.metric] = parseInt(baselineAnswers[q.id]);
            }));
        }

        // B. Clinical Flags
        const clinicalTriggers = ['anxiety', 'depression', 'trauma', 'substance', 'alcohol', 'gambling'];
        let activeClinicalFlag = null;
        let maxClinicalScore = -1;

        clinicalTriggers.forEach(cKey => {
            const val = baselineMetrics[cKey] || 0;
            if (val >= 2 && val > maxClinicalScore) {
                activeClinicalFlag = cKey;
                maxClinicalScore = val;
            }
        });

        // C. Triangulation
        const combinedScores = {};
        Object.keys(radarScores).forEach(key => {
            const radarVal = radarScores[key];
            const baseVal = baselineMetrics[key];
            combinedScores[key] = baseVal !== undefined ? Math.min(radarVal, baseVal) : radarVal;
        });
        setTriangulatedScores(combinedScores);

        // D. Find Lowest
        const sortedMetrics = Object.entries(combinedScores).sort(([,a], [,b]) => a - b);
        const lowestMetric = sortedMetrics[0]?.[0] || 'general';
        const lowestScore = sortedMetrics[0]?.[1] || 100;

        // E. Save to Context (Do this BEFORE bypassing so the Complete screen knows the score)
        let finalTarget = lowestMetric;
        let finalMode = 'performance';

        if (activeClinicalFlag) {
            finalTarget = activeClinicalFlag;
            finalMode = 'clinical';
        } else if (lowestScore < 40) {
            finalMode = 'clinical';
        }

        setAgentContext({ mode: finalMode, targetMetric: finalTarget });

        // F. Bypasses & Routing
        if (config?.audience === 'Youth') {
            handleSubmit();
            return;
        }

        if (config?.configOptions?.includeChatAgent === false) {
            handleSubmit();
            return;
        }

        setStage('clinical_gate');
    };

    // --- 5. HANDLERS ---
    const handleNextStage = (data) => {
        if (stage === 'intro') setStage('baseline');
        else if (stage === 'baseline') {
            if (data?.answers) setBaselineAnswers(data.answers);
            
            if (isPulseMode || config?.configOptions?.includeRadar === false) {
                // No radar (and no game) for this set: submit straight from the
                // answers in hand, so nothing waits on the state update above.
                handleSubmit(data?.answers);
            } else {
                setStage('radar');
            }
        } 
        else if (stage === 'radar') {
            config?.configOptions?.includeCognitive === false ? analyzeResults() : setStage('game');
        }
        else if (stage === 'game') analyzeResults();
        else if (stage === 'clinical_gate') setStage('clinical_active');
        else if (stage === 'clinical_active') handleSubmit();
    };

    const handleGameComplete = (data) => {
        setGameScores(data);
        gameScoresRef.current = data;
        setTimeout(() => handleNextStage(), 1000);
    };

    const handleStepChange = useCallback((q) => {
        setActiveQuestion(prev => (prev?.text === q.text && prev?.type === q.type) ? prev : q);
    }, []);

    return {
        stage, setStage, config, sessionId, agentContext, setAgentContext,
        activeQuestion, baselineAnswers, radarScores, setRadarScores,
        gameScores, isSubmitting, triangulatedScores, submittedStats,
        handleNextStage, handleGameComplete, handleStepChange, handleSubmit
    };
};